import React, { useEffect, useState } from 'react'
import CreditCardInput from 'react-credit-card-input'
import { refreshAccessToken } from '../../utilities/extensions'
import { company, CustomerCouponCode, customerData, PlaidAuthorization } from '../../utilities/CustomerData'
import { apiURL } from '../../utilities/URLs'
import { useNavigate } from 'react-router-dom'
import { AddPaymentConfirmation, AddPaymentDeclined } from './PaymentModals'
import { usePlaidLink } from 'react-plaid-link'
import { PlaidAuthorizationModal } from './PlaidAuthorizationModal'
import posthog from 'posthog-js'

import plaidLogo from '../../assets/plaidLogo.png'
import cardIcon from '../../assets/PaymentIcons/cardIcon.png'
import bankIcon from '../../assets/PaymentIcons/bankIcon.png'

export function AddPaymentMethod({ setShowNavBar }) {
  const navigate = useNavigate()

  const MAX_FAILED_ATTEMPTS = 3
  const LOCKOUT_TIME_MS = 60 * 60 * 1000

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })

  const [showCouponCode, setShowCouponCode] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [customerCouponCode, setCustomerCouponCode] = useState(customerData.customerCouponCode)
  const [couponError, setCouponError] = useState('')

  const [referralCode, setReferralCode] = useState('')
  const [customerReferralCode, setCustomerReferralCode] = useState(customerData.customerReferralCode)
  const [referralCodeError, setReferralCodeError] = useState('')

  const [customerInvoice, setCustomerInvoice] = useState(customerData.customerInvoiceData)
  const [errors, setErrors] = useState({})
  const [paymentMethod, setPaymentMethod] = useState(customerData.customerLeadData.leadContact.chartStatus !== 'COMPLETED' ? 'card' : 'card')
  const [payingInvoice, setPayingInvoice] = useState(false)
  const [addedPayment, setAddedPayment] = useState(false)
  const [addPaymentFailed, setAddPaymentFailed] = useState(false)
  const [addPaymentError, setAddPaymentError] = useState('')
  const [paymentCode, setPaymentCode] = useState('')
  const [retriesRemaining, setRetriesRemaining] = useState(3)

  const [plaidAuthorization, setPlaidAuthorization] = useState(new PlaidAuthorization())
  const [fetchingBankInfo, setFetchingBankInfo] = useState(false)
  const [movingCustomer, setMovingCustomer] = useState(false)

  const [plaidLinkToken, setPlaidLinkToken] = useState('')
  const [plaidPublicToken, setPlaidPublicToken] = useState('')
  const [showExit, setShowExit] = useState(false)
  const [showPublicTokenError, setShowPublicTokenError] = useState(false)

  const { open, ready } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: (public_token, metadata) => {
      setPlaidPublicToken(public_token)
    },
    onExit: async (error, metadata) => {
      if (error !== null)
        await handlePlaidLinkError(error)

      setShowExit(true)
      setTimeout(() => {
        setShowExit(false)
      }, 4000)
    }
  })

  const handleCardNumberChange = (e) => {
    setFormData({ ...formData, cardNumber: e.target.value })
  }

  const handleCardExpiryChange = (e) => {
    setFormData({ ...formData, expiryDate: e.target.value })
  }

  const handleCardCVCChange = (e) => {
    setFormData({ ...formData, cvv: e.target.value })
  }

  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value)
  }

  const handlePlaidLinkError = async (error) => {
    const form = new FormData()
    form.append('plaidLinkError', JSON.stringify({
      customerId: customerData.customerLeadData.leadContact.contactId,
      customerName: customerData.customerLeadData.leadContact.contactName,
      customerEmail: customerData.customerLeadData.leadContact.contactEmail,
      plaidLinkError: error
    }))

    const plaidLinkErrorResponse = await fetch(`${apiURL}/api/uploadPlaidLinkError`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const plaidLinkErrorData = await plaidLinkErrorResponse.json()
    if (plaidLinkErrorData.response.error) {
      if (plaidLinkErrorData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handlePlaidLinkError(error) : navigate('/', { state: { expired: true } })
      } else
        console.error(plaidLinkErrorData.response.error)
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (paymentMethod === 'card') {
      if (!formData.cardNumber) newErrors.cardNumber = 'Card Number is required'
      if (!formData.expiryDate) newErrors.expiryDate = 'Expiry Date is required'
      if (!formData.cvv) newErrors.cvv = 'CVV is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }

    const failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
    const lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

    const currentTime = new Date().getTime()

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      if (currentTime - lastFailedAttempt < LOCKOUT_TIME_MS) {
        setAddPaymentError(`You have reached the maximum number of failed payment attempts. Please try again in ${new Date(LOCKOUT_TIME_MS - (currentTime - lastFailedAttempt)).getMinutes()} minutes.`)
        setRetriesRemaining(0)
        setAddPaymentFailed(true)
        return
      } else {
        localStorage.removeItem('failedAttempts')
        localStorage.removeItem('lastFailedAttempt')
      }
    }

    setPayingInvoice(true)
    const form = new FormData()
    form.append('paymentInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      currentLeadStageId: customerData.customerLeadData.leadStage.leadStageId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      paymentType: paymentMethod,
      customerCreditCardInfo: {
        cardNumber: formData.cardNumber,
        cardExpiryDate: formData.expiryDate,
        cardCVV: formData.cvv
      },
      customerBillingInfo: {
        firstName: customerData.customerLeadData.leadContact.contactFirstName,
        lastName: customerData.customerLeadData.leadContact.contactLastName,
        email: customerData.customerLeadData.leadContact.contactEmail
      },
      customerInvoiceData: customerData.customerInvoiceData,
      customerReferralCode: customerReferralCode,
      customerCouponCode: customerCouponCode
    }))

    const storeCustomerResponse = await fetch(`${apiURL}/api/storeCustomerPayment`, {
      method: 'POST',
      body: form,
      headers: {
        'Authorization': customerData.accessToken
      }
    })
    const storeCustomerData = await storeCustomerResponse.json()
    setPayingInvoice(false)

    if (storeCustomerData.response.error) {
      if (storeCustomerData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleSubmit(e) : navigate('/', { state: { expired: true } })
      } else
        console.error(storeCustomerData.response.error)
    } else {
      if (storeCustomerData.response.addCustomerResponse.response === 'Customer Added') {
        customerInvoice.customerVaultId = storeCustomerData.response.addCustomerResponse.transactionId
        const updateTranscripts = !!(customerData.customerLeadData.leadContact.taxStatusId && !customerData.customerLeadData.leadContact.chartAccessToken)
        customerData.updateCustomerStage(updateTranscripts ? '74023762-cc67-4843-ae40-f19fb24f5bd4' : '01ac55ea-c71f-44df-8b23-3e64685ca01f', updateTranscripts ? '1eb589fb-460d-4d06-b374-dd10c1fdaa52' : '3d261241-5b93-49df-95e6-6e8bfbc75f71', updateTranscripts ? 'Docs Available - Post Paid Update Transcripts' : 'Documents Available - Postpaid')
        setAddedPayment(true)
      } else {
        const currentTime = new Date().getTime()
        let failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
        let lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

        failedAttempts++
        lastFailedAttempt = currentTime

        localStorage.setItem('failedAttempts', failedAttempts)
        localStorage.setItem('lastFailedAttempt', lastFailedAttempt)

        if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
          setAddPaymentError('You have reached the maximum number of failed payment attempts. Please try again in 60 minutes.')
          setRetriesRemaining(0)
          setPaymentCode('')
          setAddPaymentFailed(true)
        } else {
          setFormData({
            cardNumber: '',
            expiryDate: '',
            cvv: ''
          })
          setAddPaymentError(storeCustomerData.response.errorResponse)
          setPaymentCode(storeCustomerData.response.addCustomerResponse.responseCode)
          setRetriesRemaining(3 - failedAttempts)
          setAddPaymentFailed(true)
        }
        setAddPaymentError(storeCustomerData.response.errorResponse)
        setAddPaymentFailed(true)
      }
    }
  }

  const handleMoveCustomerPostPaid = async () => {
    setPlaidAuthorization(new PlaidAuthorization())
    setMovingCustomer(true)

    const customerPostPaidResponse = await fetch(`${apiURL}/api/moveCustomerToPostPaid?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
      headers: {
        'Authorization': customerData.accessToken
      }
    })
    const customerPostPaidData = await customerPostPaidResponse.json()
    setMovingCustomer(false)

    if (customerPostPaidData.response.error) {
      if (customerPostPaidData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleMoveCustomerPostPaid() : navigate('/', { state: { expired: true } })
      } else
        console.error(customerPostPaidData.response.error)
    } else {
      customerData.updateCustomerStage('01ac55ea-c71f-44df-8b23-3e64685ca01f', '3d261241-5b93-49df-95e6-6e8bfbc75f71', 'Documents Available - Postpaid')
      setAddedPayment(true)
    }
  }

  const handleCheckCouponCode = async () => {
    const form = new FormData()
    form.append('couponInfo', JSON.stringify({
      customerId: customerData.customerLeadData.leadContact.contactId,
      couponCode: couponCode,
      couponType: customerData.customerInvoiceData.invoiceTerms === 'Invoice Due On Receipt' ? 'Pre-Paid' : 'Post-Paid',
      currentLeadStageId: customerData.customerLeadData.leadStage.leadStageId
    }))

    const checkCouponResponse = await fetch(`${apiURL}/api/checkCouponCode`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const checkCouponData = await checkCouponResponse.json()
    if (checkCouponData.response.error) {
      if (checkCouponData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleCheckCouponCode() : navigate('/', { state: { expired: true } })
      } else
        setCouponError(checkCouponData.response.error)
    } else {
      setCouponError('')
      setCouponCode('')
      setCustomerCouponCode(new CustomerCouponCode({
        customerId: customerData.customerLeadData.leadContact.contactId,
        couponCode: checkCouponData.response.couponCode.couponCode,
        couponAmount: checkCouponData.response.couponCode.couponAmount,
        couponType: checkCouponData.response.couponCode.couponType
      }))
    }
  }

  const handleCheckReferralCode = async () => {
    if (referralCode === customerReferralCode.referralCode)
      setReferralCodeError('Cannot Use Own Referral Code.')
    else {
      const checkReferralCodeResponse = await fetch(`${apiURL}/api/checkReferralCode?customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}&referralCode=${encodeURIComponent(referralCode)}&isCheckout=1`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const checkReferralCodeData = await checkReferralCodeResponse.json()
      if (checkReferralCodeData.response.error) {
        if (checkReferralCodeData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleCheckReferralCode() : navigate('/', { state: { expired: true } })
        } else
          setReferralCodeError(checkReferralCodeData.response.error)
      } else {
        setReferralCodeError('')
        setReferralCode('')
        setCustomerReferralCode(prevReferralCode => (
          {
            ...prevReferralCode,
            appliedReferralCode: checkReferralCodeData.response.referralCode
          }
        ))
      }
    }
  }

  useEffect(() => {
    const handleGetLinkToken = async () => {
      const linkTokenResponse = await fetch(`${apiURL}/api/createPlaidLinkToken?customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const linkTokenData = await linkTokenResponse.json()
      if (linkTokenData.response.error) {
        if (linkTokenData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetLinkToken() : navigate('/', { state: { expired: true } })
        } else
          console.error(linkTokenData.response.error)
      } else
        setPlaidLinkToken(linkTokenData.response.linkToken)
    }

    document.title = `${company.companyName} | Add Payment Method`
    handleGetLinkToken()
  }, [])

  useEffect(() => {
    const handleExchangePublicToken = async () => {
      setFetchingBankInfo(true)

      const exchangePublicTokenResponse = await fetch(`${apiURL}/api/exchangePublicToken?publicToken=${encodeURIComponent(plaidPublicToken)}&customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}&customerName=${encodeURIComponent(customerData.customerLeadData.leadContact.contactName)}&paymentAmount=${encodeURIComponent(customerData.customerInvoiceData.invoiceRemainingBalance)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const exchangePublicTokenData = await exchangePublicTokenResponse.json()
      setFetchingBankInfo(false)
      if (exchangePublicTokenData.response.error) {
        if (exchangePublicTokenData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleExchangePublicToken() : navigate('/', { state: { expired: true } })
        } else {
          console.error(exchangePublicTokenData.response.error)
          setShowPublicTokenError(true)
          setTimeout(() => {
            setShowPublicTokenError(false)
          }, 4000)
        }
      } else
        setPlaidAuthorization(new PlaidAuthorization(exchangePublicTokenData.response))
    }

    if (plaidPublicToken.length > 0)
      handleExchangePublicToken()
  }, [plaidPublicToken])

  useEffect(() => {
    setShowNavBar(false)
    posthog.startSessionRecording()
    return () => {
      setShowNavBar(true)
      posthog.stopSessionRecording()
    }
  }, [])

  return (
    <div>
      <div className='flex justify-center w-full'>
        <div className='flex flex-col items-center'>
          <div className='p-4 w-full h-fit mt-10 flex lg:flex-row flex-col mb-10 justify-center gap-4 font-mont'>
            <div className='mb-6'>
              <h3 className='text-xl font-medium mb-4'>Enter Customer Details</h3>
              <div className='border p-6 rounded-md bg-gray-50 max-w-[30rem]'>
                <p>We will deduct the 20% fee AFTER you receive your check from the IRS. You will also have the option to pay your invoice at anytime in your customer portal</p>
                {showCouponCode && (
                  <div className='flex flex-col gap-4'>
                    <div className='bg-blue-50 shadow-lg rounded-md p-4'>
                      <label htmlFor='couponCode' className='block text-sm font-medium text-gray-700 mb-2'>
                        Apply Coupon Code
                      </label>
                      {couponError && <p className='text-red-500 text-xs mt-2'>{couponError}</p>}
                      {customerCouponCode.couponCode.length > 0 ? (
                        <div className='flex flex-row gap-4 items-center mt-4'>
                          <p>Applied Coupon Code:</p>
                          <p className='font-bold'>{customerCouponCode.couponCode}</p>
                        </div>
                      ) : (
                        <div className='flex items-center'>
                          <input
                            type='text'
                            id='couponCode'
                            name='couponCode'
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            className='p-2 border border-gray-300 rounded-l-md w-full'
                            placeholder='Enter coupon code'
                          />
                          <button
                            onClick={() => handleCheckCouponCode()}
                            className={`text-white p-2 rounded-r-md ${couponCode.length === 0 ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 transition-colors'}`}
                            disabled={couponCode.length === 0}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                    <div className='bg-blue-50 shadow-lg rounded-md p-4'>
                      <label htmlFor='couponCode' className='block text-sm font-medium text-gray-700 mb-2'>
                        Apply Referral Code
                      </label>
                      {referralCodeError && <p className='text-red-500 text-xs mt-2'>{referralCodeError}</p>}
                      {customerReferralCode.appliedReferralCode.length > 0 ? (
                        <div className='flex flex-row gap-4 items-center mt-4'>
                          <p>Applied Referral Code:</p>
                          <p className='font-bold'>{customerReferralCode.appliedReferralCode}</p>
                        </div>
                      ) : (
                        <div className='flex items-center'>
                          <input
                            type='text'
                            id='couponCode'
                            name='couponCode'
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value)}
                            className='p-2 border border-gray-300 rounded-l-md w-full'
                            placeholder='Enter referral code'
                          />
                          <button
                            onClick={() => handleCheckReferralCode()}
                            className={`text-white p-2 rounded-r-md ${referralCode.length === 0 ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 transition-colors'}`}
                            disabled={referralCode.length === 0}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='lg:w-[25rem] p-6 bg-white shadow-lg rounded-lg h-fit'>
              <h2 className='text-2xl font-semibold mb-6'>Payment Information</h2>
              {customerData.customerLeadData.leadContact.chartStatus === 'COMPLETED' && (
                <div className='w-full mb-4'>
                  <label className='block text-sm font-medium text-gray-700'>Payment Method</label>
                  {paymentMethod === '' && <p>Please choose a payment method.</p>}
                  <div className='w-full flex items-center mt-2 space-x-4'>
                    <button
                      type='button'
                      onClick={() => handlePaymentMethodChange('card')}
                      className={`w-1/2 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium ${paymentMethod === 'card' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                      <img src={cardIcon} alt='Credit Card' className='w-5 h-5 mr-2' />
                      Credit Card
                    </button>
                    <button
                      type='button'
                      disabled
                      onClick={() => handlePaymentMethodChange('ach')}
                      className={`cursor-not-allowed w-1/2 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium ${paymentMethod === 'ach' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                      <img src={bankIcon} alt='Bank' className='w-5 h-5 mr-2' />
                      Bank
                    </button>
                  </div>
                </div>
              )}
              {paymentMethod === 'card' ? (
                <>
                  <div className='mb-4 flex flex-col items-center'>
                    <label htmlFor='cardNumber' className='text-sm font-medium text-gray-700 text-start w-full'>Card Number</label>
                    <CreditCardInput
                      cardNumberInputProps={{
                        value: formData.cardNumber,
                        onChange: handleCardNumberChange,
                      }}
                      cardExpiryInputProps={{
                        value: formData.expiryDate,
                        onChange: handleCardExpiryChange,
                      }}
                      cardCVCInputProps={{
                        value: formData.cvv,
                        onChange: handleCardCVCChange,
                      }}
                      fieldClassName='mt-2 p-2 border border-gray-300 rounded-md'
                    />
                    {errors.cardNumber && <p className='text-red-500 text-xs mt-1'>{errors.cardNumber}</p>}
                    {errors.expiryDate && <p className='text-red-500 text-xs mt-1'>{errors.expiryDate}</p>}
                    {errors.cvv && <p className='text-red-500 text-xs mt-1'>{errors.cvv}</p>}
                  </div>
                  <button
                    type='submit'
                    onClick={handleSubmit}
                    className={`w-full text-white p-2 rounded-md ${payingInvoice ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
                    disabled={payingInvoice}
                  >
                    {payingInvoice ? 'Processing...' : 'Submit Payment Information'}
                  </button>
                </>
              ) : paymentMethod === 'ach' ? (
                <div className='w-full flex flex-col items-center gap-4'>
                  <img className='w-32' src={plaidLogo} />
                  <button
                    type='submit'
                    onClick={() => { setShowNavBar(false); open() }}
                    className={`w-full text-white p-2 rounded-md mt-2 ${!ready || payingInvoice || fetchingBankInfo || movingCustomer ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
                    disabled={!ready || payingInvoice || fetchingBankInfo || movingCustomer}
                  >
                    Connect Bank Account
                  </button>
                  {fetchingBankInfo && (
                    <div className='flex flex-row items-center justify-center gap-2'>
                      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                      <p>Grabbing Banking Information...</p>
                    </div>
                  )}
                  {showExit && (
                    <p className='text-sm text-red-500'>Oops! It looks like you left before we could finish connecting your account. No worries—please give it another try!</p>
                  )}
                  {showPublicTokenError && (
                    <p className='text-sm text-red-500'>Uh-oh! We ran into a little hiccup while linking your bank information. Please give it another try!</p>
                  )}
                  {payingInvoice && (
                    <div className='flex flex-row items-center justify-center gap-2'>
                      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                      <p>Paying Invoice, Please do not leave or refresh this page...</p>
                    </div>
                  )}
                  {movingCustomer && (
                    <div className='flex flex-row items-center justify-center gap-2'>
                      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                      <p>Finalizing Banking Information...</p>
                    </div>
                  )}
                </div>
              ) : (
                null
              )}
            </div>
          </div>
        </div>
      </div>
      <AddPaymentConfirmation
        isOpen={addedPayment}
      />
      <AddPaymentDeclined
        isOpen={addPaymentFailed}
        setIsOpen={setAddPaymentFailed}
        paymentCode={paymentCode}
        paymentError={addPaymentError}
        retriesRemaining={retriesRemaining}
      />
      {plaidAuthorization.accessToken && (
        <PlaidAuthorizationModal
          _plaidAuthorization={plaidAuthorization}
          paymentAmount={customerInvoice.invoiceRemainingBalance}
          handleAgreement={handleMoveCustomerPostPaid}
        />
      )}
    </div>
  )
}
